import React, { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Typography, Select, Button, Space, Empty } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useSnackbar } from 'notistack';
import * as Sentry from "@sentry/react";
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/PageLayout';
import { usePageLayout } from '../hooks/usePageLayout';
import { getAuditLogs } from '../apiCalls';
import { isSuperAdmin } from '../utils/roleUtils';

const ACTION_COLORS = {
    LOGIN: 'blue',
    CREATE_USER: 'green',
    UPDATE_PRICING: 'orange',
    UPDATE_PDF_CONTENT: 'purple',
    UPDATE_PDF_LAYOUT: 'purple',
    UPDATE_EMAIL_CONFIG: 'cyan',
};

const PAGE_SIZE = 50;

const AuditLog = () => {
    const { t } = useTranslation();
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [filterProperty, setFilterProperty] = useState(null);
    const pageLayoutProps = usePageLayout();
    const { role, propList } = pageLayoutProps;
    const { enqueueSnackbar } = useSnackbar();

    const ACTION_LABELS = {
        LOGIN: t('audit.actions.login'),
        CREATE_USER: t('audit.actions.createUser'),
        UPDATE_PRICING: t('audit.actions.updatePricing'),
        UPDATE_PDF_CONTENT: t('audit.actions.updatePdfContent'),
        UPDATE_PDF_LAYOUT: t('audit.actions.updatePdfLayout'),
        UPDATE_EMAIL_CONFIG: t('audit.actions.updateEmailConfig'),
    };

    useEffect(() => {
        if (role && !isSuperAdmin(role)) {
            window.location.href = '/';
            return;
        }
    }, [role]);

    const fetchLogs = useCallback(async (pg = 1, property = filterProperty) => {
        if (!isSuperAdmin(role)) {
            return;
        }

        setLoading(true);
        try {
            const data = await getAuditLogs({
                property: property || undefined,
                limit: PAGE_SIZE,
                offset: (pg - 1) * PAGE_SIZE,
            });
            setLogs(data.logs);
            setTotal(data.total);
        } catch (err) {
            enqueueSnackbar(t('audit.error'), { variant: 'error' });
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    }, [filterProperty, enqueueSnackbar, role, t]);

    useEffect(() => {
        fetchLogs(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePropertyFilter = (value) => {
        setFilterProperty(value);
        setPage(1);
        fetchLogs(1, value);
    };

    const handlePageChange = (pg) => {
        setPage(pg);
        fetchLogs(pg);
    };

    const columns = [
        {
            title: t('audit.columns.dateTime'),
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (ts) => new Date(ts).toLocaleString('en-GB'),
        },
        {
            title: t('audit.columns.user'),
            dataIndex: 'userEmail',
            key: 'userEmail',
            ellipsis: true,
        },
        {
            title: t('audit.columns.action'),
            dataIndex: 'action',
            key: 'action',
            width: 180,
            render: (action) => (
                <Tag color={ACTION_COLORS[action] || 'default'}>
                    {ACTION_LABELS[action] || action}
                </Tag>
            ),
        },
        {
            title: t('audit.columns.property'),
            dataIndex: 'propertyId',
            key: 'propertyId',
            width: 140,
            render: (id) => {
                if (!id) return <Typography.Text type="secondary">—</Typography.Text>;
                const found = propList.find(p => p.propertyID === id);
                return found ? found.companyName : id;
            },
        },
        {
            title: t('audit.columns.details'),
            dataIndex: 'details',
            key: 'details',
            ellipsis: true,
            render: (details) => {
                if (!details) return null;
                return (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {Object.entries(details).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                    </Typography.Text>
                );
            },
        },
    ];

    return (
        <PageLayout {...pageLayoutProps}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <Typography.Title level={2} style={{ margin: 0 }}>{t('audit.title')}</Typography.Title>
                    <Space>
                        {isSuperAdmin(role) && (
                            <Select
                                placeholder={t('audit.filterProperty')}
                                allowClear
                                style={{ width: 200 }}
                                value={filterProperty}
                                onChange={handlePropertyFilter}
                                options={propList.map(p => ({ value: p.propertyID, label: p.companyName }))}
                            />
                        )}
                        <Button icon={<ReloadOutlined />} onClick={() => fetchLogs(page)}>
                            {t('common.refresh')}
                        </Button>
                    </Space>
                </div>

                {logs.length === 0 && !loading ? (
                    <Empty description={t('audit.noEntries')} />
                ) : (
                    <Table
                        dataSource={logs}
                        columns={columns}
                        rowKey="_id"
                        loading={loading}
                        pagination={{
                            current: page,
                            pageSize: PAGE_SIZE,
                            total,
                            onChange: handlePageChange,
                            showTotal: (total) => t('audit.totalEntries', { total }),
                        }}
                        scroll={{ x: 'max-content' }}
                        style={{ background: 'white', borderRadius: 8 }}
                    />
                )}
            </div>
        </PageLayout>
    );
};

export default AuditLog;
